import { sendDocumentSchema } from "@erp/shared";
import { queryOptions, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import { toast } from "sonner";
import { Badge, type BadgeTone } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { Field } from "~/components/ui/field";
import { Input, Textarea } from "~/components/ui/input";
import { Sheet } from "~/components/ui/sheet";
import { Skeleton } from "~/components/ui/states";
import { api, ApiError, unwrap } from "~/lib/api";
import { errorMessageKey, fieldErrors } from "~/lib/errors";
import { errorAt, schemaErrors, type ErrorMap } from "~/lib/forms";
import { useFormat, useT } from "~/lib/i18n";

export type MailableKind = "quotes" | "invoices" | "purchasing";

export const companyQuery = queryOptions({
  queryKey: ["settings", "company"],
  queryFn: () => unwrap(api.settings.company.get()),
  staleTime: 5 * 60_000,
});

type EmailBody = { to: string; cc?: string | null; subject: string; message: string };

/** Same three endpoints (draft, history, send) under each mailable document. */
function endpoints(kind: MailableKind, id: string) {
  const route =
    kind === "quotes" ? api.quotes({ id }) : kind === "invoices" ? api.invoices({ id }) : api.purchasing.orders({ id });
  return {
    draft: () => unwrap(route.email.get()),
    history: () => unwrap(route.emails.get()),
    send: (body: EmailBody) => unwrap(route.send.post(body)),
  };
}

export function SendEmailSheet({
  kind,
  id,
  open,
  onOpenChange,
}: {
  kind: MailableKind;
  id: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const t = useT();
  return (
    <Sheet open={open} onOpenChange={onOpenChange} title={t("email.title")} description={t("email.attachment")}>
      {open ? <SendEmailForm kind={kind} id={id} onDone={() => onOpenChange(false)} /> : null}
    </Sheet>
  );
}

function SendEmailForm({ kind, id, onDone }: { kind: MailableKind; id: string; onDone: () => void }) {
  const t = useT();
  const queryClient = useQueryClient();
  const draft = useQuery({ queryKey: [kind, id, "email-draft"], queryFn: () => endpoints(kind, id).draft() });
  if (draft.isPending) return <Skeleton className="h-64 w-full" />;
  if (draft.isError) return <p className="text-sm text-danger">{t(errorMessageKey(draft.error))}</p>;
  return (
    <EmailFields
      initial={draft.data}
      onSend={(body) => endpoints(kind, id).send(body)}
      onSent={() => {
        void queryClient.invalidateQueries({ queryKey: [kind, id, "emails"] });
        onDone();
      }}
    />
  );
}

function EmailFields({
  initial,
  onSend,
  onSent,
}: {
  initial: { to: string; subject: string; message: string };
  onSend: (body: { to: string; cc?: string | null; subject: string; message: string }) => Promise<unknown>;
  onSent: () => void;
}) {
  const t = useT();
  const [to, setTo] = useState(initial.to);
  const [cc, setCc] = useState("");
  const [subject, setSubject] = useState(initial.subject);
  const [message, setMessage] = useState(initial.message);
  const [submitted, setSubmitted] = useState(false);
  const [serverErrors, setServerErrors] = useState<ErrorMap>({});
  const [notConfigured, setNotConfigured] = useState(false);
  const values = { to, cc, subject, message };
  const errors = { ...serverErrors, ...(submitted ? schemaErrors(sendDocumentSchema, values) : {}) };

  const send = useMutation({
    mutationFn: () => onSend(sendDocumentSchema.parse(values)),
    onSuccess: () => {
      toast.success(t("email.queued"));
      onSent();
    },
    onError: (error) => {
      if (error instanceof ApiError && error.body?.code === "email.not_configured") return setNotConfigured(true);
      setServerErrors(fieldErrors(error));
      toast.error(t(errorMessageKey(error)));
    },
  });

  const submit = (event: FormEvent) => {
    event.preventDefault();
    setSubmitted(true);
    if (Object.keys(schemaErrors(sendDocumentSchema, values)).length) return;
    send.mutate();
  };

  return (
    <form onSubmit={submit} noValidate className="flex flex-col gap-4">
      {notConfigured ? (
        <p className="rounded-[10px] bg-warning-soft px-3.5 py-3 text-sm text-warning">{t("email.notConfigured")}</p>
      ) : null}
      <Field label={t("email.to")} error={errorAt(t, errors, "to")}>
        <Input type="email" value={to} onChange={(e) => setTo(e.target.value)} />
      </Field>
      <Field label={t("email.cc")} optionalLabel={t("common.states.optional")} error={errorAt(t, errors, "cc")}>
        <Input type="email" value={cc} onChange={(e) => setCc(e.target.value)} />
      </Field>
      <Field label={t("email.subject")} error={errorAt(t, errors, "subject")}>
        <Input value={subject} onChange={(e) => setSubject(e.target.value)} />
      </Field>
      <Field label={t("email.message")} error={errorAt(t, errors, "message")}>
        <Textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={9} />
      </Field>
      <Button type="submit" variant="primary" loading={send.isPending} className="self-start">
        {t("email.send")}
      </Button>
    </form>
  );
}

const EMAIL_TONE: Record<"queued" | "sent" | "failed", BadgeTone> = { queued: "neutral", sent: "success", failed: "danger" };

export function EmailHistory({ kind, id }: { kind: MailableKind; id: string }) {
  const t = useT();
  const format = useFormat();
  const history = useQuery({ queryKey: [kind, id, "emails"], queryFn: () => endpoints(kind, id).history() });
  if (!history.data?.length) return null;
  return (
    <section>
      <h3 className="mb-2 text-sm font-medium">{t("email.history")}</h3>
      <ul className="flex flex-col gap-2 text-sm">
        {history.data.map((e) => (
          <li key={e.id} className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <p className="truncate">{e.to}</p>
              <p className="text-xs text-fg-3">{format.date(e.createdAt, "datetime")}</p>
            </div>
            <Badge tone={EMAIL_TONE[e.status]}>{t(`email.statuses.${e.status}`)}</Badge>
          </li>
        ))}
      </ul>
    </section>
  );
}
